<template>
  <div
    class="main-collection critical-component-clear"
    :class="{
      'section-styles--bottom-padding': section.bottomPadding,
      'section-styles--divider': section.sectionDividers,
      'section-styles--top-padding': section.topPadding,
    }"
  >
    <div class="main-collection__header container-no-padding">
      <breadcrumbs
        class="main-collection__breadcrumbs"
        :template="liquid.template"
        :title="collectionTitle"
      />

      <div
        v-if="liquid.image.src && !hideDescription"
        class="main-collection__hero-image"
      >
        <art-direction
          block-class="main-collection"
          desktop-breakpoint="m"
          :image="liquid.mobileImage"
          :image-desktop="liquid.image"
          :lazyload="false"
          :padding="false"
        />
      </div>
    </div>

    <div class="main-collection__content text-center container">
      <h1
        id="template-title"
        class="
          main-collection__title
          text-heading-l
          text-heading-2xl-desktop
        "
        v-text="collectionTitle"
      />

      <div
        v-if="formatDescription && !hideDescription"
        class="main-collection__description-wrapper"
      >
        <div
          class="main-collection__description"
          v-html="formatDescription"
        />

        <span
          class="visually-hidden"
          aria-hidden="true"
          v-html="liquid.description"
        />

        <btn
          v-if="showDescriptionBtn"
          class="main-collection__toggle"
          block-class="main-collection"
          :show-icon="false"
          :label="buttonText"
          label-class="text-label-s"
          @click="showHideFullDescription"
        />
      </div>
    </div>

    <nav
      v-if="subNavigation.handle"
      class="main-collection__navigation container"
    >
      <ul class="main-collection__navigation-list">
        <li
          v-for="(item) of subNavigation.items"
          :key="item.handle"
          class="main-collection__navigation-item"
        >
          <btn
            class="
              main-collection__misc-navigation-link
              main-collection__misc-navigation-link--button
            "
            :label="item.title"
            label-class="text-heading-3xs text-heading-2xs-desktop"
            modifiers="outlined"
            :show-icon="false"
            :url="item.url"
          />
        </li>
      </ul>
    </nav>

    <boost-grid
      class="main-collection__boost"
      :in-grid-content-banner="inGridContentBanner"
      :liquid="liquid"
      :products-per-page="productsPerPage"
      :seo-title="seoTitle"
      :sort-by-options="sortByOptions"
      :storage-key="storageKey"
    />
  </div>
</template>

<script>

/**
 * Vue: Main collection (main-collection)
 * -----------------------------------------------------------------------------
 * Main collection page component.
 *
 * @param {Object} inGridContentBanner - In-grid content banner metafields.
 * @param {Object} liquid - Liquid data.
 * @param {Number} productsPerPage - Products per page based on in-grid content
 * banner width.
 * @param {Object} section - Section settings.
 * @param {Object} sortByOptions - Sort by options.
 * @param {Object} subNavigation - Sub-navigation settings.
 */
import { mapState } from 'vuex'

import ArtDirection from '~global/images/art-direction'
import BoostGrid from '~async/boost-grid/boost-grid'
import Breadcrumbs from '~async/breadcrumbs/breadcrumbs'
import Btn from '~global/btn/btn'

export default {
  name: 'MainCollection',

  components: {
    ArtDirection,
    BoostGrid,
    Breadcrumbs,
    Btn,
  },

  props: {
    inGridContentBanner: {
      type: Object,
      default: () => ({
        bannerImage: '',
        bannerLink: '',
        bannerText: '',
        bannerVideo: '',
        bannerWidth: '',
      }),
    },
    liquid: {
      type: Object,
      default: () => ({
        description: '',
        handle: '',
        image: '',
        title: '',
        url: '',
      }),
    },
    productsPerPage: {
      type: Number,
      default: 16,
    },
    section: {
      type: Object,
      default: () => ({
        bottomPadding: true,
        sectionDividers: true,
        topPadding: false,
      }),
    },
    seoTitle: {
      type: String,
      default: '',
    },
    sortByOptions: {
      type: Object,
      default: () => ({}),
    },
    subNavigation: {
      type: Object,
      default: () => ({
        handle: '',
        items: [],
      }),
    },
  },

  data() {
    return {
      descriptionState: true,
      formatDescription: '',
      storageKey: 'collection-temp',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      choices: (state) => state['main-collection'].choices,
    }),

    /**
     * Compute read more/less text.
     * @returns {String}
     */
    buttonText() {
      return this.descriptionState
        ? this.$string('accessibility.show_more')
        : this.$string('accessibility.show_less')
    },

    /**
     * Compute description length.
     * @returns {Number}
     */
    descriptionLength() {
      return this.liquidDescription
        ? this.liquidDescription.split(/\s+/).length
        : 0
    },

    /**
     * Compute Liquid description to add text classes.
     * @returns {String}
     */
    liquidDescription() {
      return this.liquid.description?.replaceAll('<p', '<p class="text-body-2xs text-body-s-desktop"')
    },

    /**
     * Compute if description read more button should be shown.
     * @returns {Boolean}
     */
    showDescriptionBtn() {
      return this.descriptionLength > 35
    },

    /**
     * Hide the description if the page number is greater than 1.
     */
    hideDescription() {
      if (!this.choices.includes('page=')) {
        return false
      }

      let pageNumber = 1

      const choicesArray = this.choices.split('&').filter((choice) => {
        return choice !== ''
      })

      choicesArray.forEach((choice) => {
        if (choice.includes('page')) {
          pageNumber = Number(choice.split('=')[1])
        }
      })

      return pageNumber > 1
    },

    /**
     * Return collection title.
     * @returns {String}
     */
    collectionTitle() {
      return this.liquid.title.replaceAll('&amp;', '&')
    },
  },

  mounted() {
    this.formatDescription = this.liquidDescription
      ? `${this.liquidDescription.slice(0, 245)}...`
      : ''
  },

  methods: {

    /**
     * Set collection description format.
     */
    showHideFullDescription() {
      if (this.descriptionState) {
        this.descriptionState = false
        this.formatDescription = this.liquidDescription
        return
      }

      this.descriptionState = true
      this.formatDescription = `${this.liquidDescription.slice(0, 245)}...`
    },
  },
}
</script>

<style lang="scss">
@import './main-collection';
@import '~async/boost-grid/boost-grid';
@import '~async/product-card/product-card';
@import '~async/boost-grid/boost-filter';
@import '~async/boost-grid/boost-pagination';
@import '~async/in-grid-content-banner/in-grid-content-banner';
@import '~async/boost-drawer/boost-drawer';
</style>
